import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "twin.macro";
import { runOneSignal } from "../OneSignal";
import clientIdStorage from "../storage";
import { Auth0Jwt } from "./Auth0Jwt";
import { Permission } from "./Permission";

interface PermissionsContextType {
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

const PermissionsProvider = (props: { children: ReactNode }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const oneSignalStarted = useRef(false);

  useEffect(() => {
    const extractPermissions = async (): Promise<void> => {
      try {
        const token = await getAccessTokenSilently({ cacheMode: "cache-only" });
        const decoded = jwtDecode<Auth0Jwt>(token);
        setPermissions(decoded.permissions ?? []);

        if (decoded.user_metadata?.client_id) {
          clientIdStorage.set(decoded.user_metadata?.client_id as string);

          if (!oneSignalStarted.current) {
            oneSignalStarted.current = true;
            void runOneSignal();
          }
        }
      } catch (error) {
        console.warn("Unable to get access token", error);
      }
    };

    void extractPermissions();
  }, [getAccessTokenSilently]);

  const hasPermission = (permission: Permission) =>
    permissions.includes(permission);

  return (
    <PermissionsContext.Provider
      value={{ permissions, hasPermission }}
      {...props}
    />
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === null) {
    throw new Error(
      "usePermissions has to be used within <PermissionsProvider />"
    );
  }
  return context;
};

export default PermissionsProvider;
